import React, { useState, useEffect } from 'react';
import { apiService } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Activity, Database, FileJson, Calendar, CheckCircle2, XCircle, Clock,
  AlertTriangle, TrendingUp, Server, Download, Upload, ArrowUpDown
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, logsRes, alertsRes] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getRecentLogs(10),
        apiService.getAlerts()
      ]);
      setStats(statsRes.data);
      setRecentLogs(logsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="badge-success"><CheckCircle2 className="h-3 w-3 mr-1" />Success</Badge>;
      case 'failed':
        return <Badge className="badge-error"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'running':
        return <Badge className="badge-warning"><Clock className="h-3 w-3 mr-1" />Running</Badge>;
      default:
        return <Badge className="badge-neutral">{status}</Badge>;
    }
  };

  const getJobTypeBadge = (jobType) => {
    if (jobType === 'fetch') {
      return <Badge className="bg-purple-50 text-purple-700 border border-purple-200"><Download className="h-3 w-3 mr-1" />Fetch</Badge>;
    }
    return <Badge className="bg-blue-50 text-blue-700 border border-blue-200"><Upload className="h-3 w-3 mr-1" />Post</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="dashboard-page">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="stat-card" data-testid="stat-total-jobs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Total Jobs</p>
                <p className="stat-value">{stats?.total_jobs || 0}</p>
                <p className="text-xs text-teal-600 mt-1">
                  {stats?.active_jobs || 0} active
                </p>
              </div>
              <div className="h-12 w-12 bg-teal-50 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-success-24h">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Success (24h)</p>
                <p className="stat-value text-emerald-600">{stats?.success_24h || 0}</p>
                <p className="text-xs text-emerald-600 mt-1">
                  <TrendingUp className="h-3 w-3 inline mr-1" />Completed
                </p>
              </div>
              <div className="h-12 w-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-failed-24h">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Failed (24h)</p>
                <p className="stat-value text-red-600">{stats?.failed_24h || 0}</p>
                <p className="text-xs text-red-600 mt-1">Requires attention</p>
              </div>
              <div className="h-12 w-12 bg-red-50 rounded-lg flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-pending">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Pending</p>
                <p className="stat-value text-amber-600">{stats?.pending_24h || 0}</p>
                <p className="text-xs text-amber-600 mt-1">Currently processing</p>
              </div>
              <div className="h-12 w-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bi-directional Sync Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 bg-blue-50 rounded-lg flex items-center justify-center">
                <Upload className="h-7 w-7 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-slate-900">Posted to Tally (24h)</p>
                  <Badge className="bg-blue-50 text-blue-700">{stats?.post_jobs || 0} jobs</Badge>
                </div>
                <p className="text-3xl font-bold text-blue-600 mt-1">{stats?.records_posted_24h || 0}</p>
                <p className="text-xs text-slate-500">records sent to Tally</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 bg-purple-50 rounded-lg flex items-center justify-center">
                <Download className="h-7 w-7 text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-slate-900">Fetched from Tally (24h)</p>
                  <Badge className="bg-purple-50 text-purple-700">{stats?.fetch_jobs || 0} jobs</Badge>
                </div>
                <p className="text-3xl font-bold text-purple-600 mt-1">{stats?.records_fetched_24h || 0}</p>
                <p className="text-xs text-slate-500">records synced to database</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
          <Server className="h-5 w-5 text-teal-600" />
          <div>
            <p className="text-xs text-slate-500">Tally Connections</p>
            <p className="font-semibold text-slate-900">{stats?.tally_connections || 0}</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
          <Database className="h-5 w-5 text-teal-600" />
          <div>
            <p className="text-xs text-slate-500">DB Connections</p>
            <p className="font-semibold text-slate-900">{stats?.db_connections || 0}</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
          <Activity className="h-5 w-5 text-teal-600" />
          <div>
            <p className="text-xs text-slate-500">Datasets</p>
            <p className="font-semibold text-slate-900">{stats?.total_datasets || 0}</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
          <FileJson className="h-5 w-5 text-teal-600" />
          <div>
            <p className="text-xs text-slate-500">XML Templates</p>
            <p className="font-semibold text-slate-900">{stats?.total_templates || 0}</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
          <ArrowUpDown className="h-5 w-5 text-teal-600" />
          <div>
            <p className="text-xs text-slate-500">Fetch Configs</p>
            <p className="font-semibold text-slate-900">{stats?.total_fetch_configs || 0}</p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="lg:col-span-2" data-testid="recent-activity">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Activity className="h-5 w-5 text-teal-600" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              {recentLogs.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No recent activity</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`h-2 w-2 rounded-full ${
                          log.status === 'success' ? 'bg-emerald-500' :
                          log.status === 'failed' ? 'bg-red-500' : 'bg-amber-500'
                        }`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm text-slate-900">{log.job_name || log.action}</p>
                            {getJobTypeBadge(log.job_type)}
                          </div>
                          <p className="text-xs text-slate-500">
                            {log.timestamp && format(parseISO(log.timestamp), 'MMM d, yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(log.records_processed > 0 || log.records_inserted > 0) && (
                          <span className="text-xs text-slate-500">
                            {log.job_type === 'fetch' 
                              ? `${log.records_inserted || 0}+ / ${log.records_updated || 0}~`
                              : `${log.records_success}/${log.records_processed}`}
                          </span>
                        )}
                        {getStatusBadge(log.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card data-testid="alerts-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Alerts
              {alerts.length > 0 && <Badge variant="destructive" className="ml-2">{alerts.length}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              {alerts.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500 opacity-50" />
                  <p>No alerts</p>
                  <p className="text-sm">All systems operational</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div key={alert.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm text-red-900">{alert.title}</p>
                          <p className="text-xs text-red-700 mt-1">{alert.message}</p>
                          <p className="text-xs text-red-500 mt-1">
                            {alert.timestamp && format(parseISO(alert.timestamp), 'MMM d, HH:mm')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
